import { useCallback, useEffect, useRef, useState } from 'react'
import { artFor } from '../packArt'
import { twMerge } from 'tailwind-merge'
import { ACCENT } from '~/components/ui'
import { Pack } from '../api/packs'
import { Pack3D } from './Pack3D'
import * as sound from '../sound'

// The BR1 skull, the mark the site is known by. On the one button that costs
// money it reads as ours in a way a generic open-box glyph does not.
//
// This is the site-wide skull (the brand gradient one), forced to flat white at
// render. The other candidate, `features/career/assets/skull.png`, is already
// white but painted at alpha 31/255 -- it is a watermark meant to sit faintly
// behind a block of text, and on the button it came out a ghost next to solid
// white type. This one is fully opaque, so `brightness(0) invert(1)` turns it
// into a clean white silhouette while the eye and nose cut-outs stay
// transparent.
const skull = new URL('../../../../assets/br1_skull.png', import.meta.url).href

type Props = {
    pack: Pack
    busy: boolean
    onOpen: () => void
    /** Why this account may not buy -- a freeze, a store block, the trust gate or the integrity
     *  gate. Set means the action is dead and this sentence is shown in place of the stock line.
     *  Read from `usePacksGate`, which asks the same code the open endpoint refuses with. */
    blockedReason?: string
    /** During the cinematic launch, fade the name/stock/action out with the
     *  rest of the catalogue while the pack itself takes the stage. */
    launching?: boolean
    /** Strip everything that is not the pack: no name, no stock line, no
     *  sold-out plate. For looking at the model and the opening sequence
     *  without the shop around it.
     *
     *  Deliberately opt-in rather than the default. On the real page every one
     *  of those carries something a player is owed before they spend -- what
     *  this pack is, how many are left, and whether it can be bought at all --
     *  and a stage that quietly dropped the sold-out plate would take money for
     *  a pack with nothing in it. */
    bare?: boolean
}

/** The height of one row of the action block, shared with the rail's info
 *  buttons. Changing it here alone breaks the alignment the two blocks are
 *  built on — change both. */
export const ACTION_ROW_H = 'h-[3.25rem]'

/**
 * The centre stage: the selected pack shown large, with the open action beneath
 * it. This is the focal point of the page, the way the foil pack is on the
 * reference — the catalogue is a left-rail chooser, and the pack itself is what
 * the player looks at while deciding.
 *
 * Sized to its container rather than to a fixed pixel height, so the page can
 * hand it whatever vertical room is left and the whole screen fits without
 * scrolling. The action block at the bottom is two rows of `ACTION_ROW_H`, which
 * is the same pair of heights the left rail's info buttons use: both blocks are
 * bottom-anchored in the same grid row, so Spin lands level with "See top
 * skins" on its own rather than by a number tuned to one viewport.
 */
/** How long the pack has to be held before it opens. */
const HOLD_MS = 2000

/** A press shorter than this never starts the hold at all.
 *
 *  Without it every flick of the pack to spin it would begin charging the ring,
 *  and the player would spend the whole time they are looking at the model
 *  dismissing a progress circle they did not ask for. */
const HOLD_GRACE_MS = 300

/** Past this the press is a drag, not a hold.
 *
 *  The same pointer does two jobs on this element -- spin the pack, and open it
 *  -- and the only thing separating them is whether it moved. Slabz gets away
 *  without this because its pack does not spin under the finger; ours does. */
const DRAG_CANCEL_PX = 8

export const PackStage = ({ pack, busy, onOpen, launching = false, bare = false, blockedReason }: Props) => {
    const soldOut = pack.is_enabled && !pack.in_stock
    // Defensive only: the catalogue no longer sends switched-off packs, so
    // a player cannot reach this stage with one.
    const unreleased = !pack.is_enabled
    // A gate refusal makes the pack unavailable in exactly the way sold-out does: the action is
    // dead and the reason sits under it. Folded into `available` rather than checked separately so
    // the hold-to-open gesture (which reads `available` at line ~130) is closed by it too --
    // otherwise the button greys out and the pack still rips when you press and hold it.
    const available = (bare || (!soldOut && !unreleased)) && !blockedReason

    // ---- Hold to open ------------------------------------------------------
    //
    // The button below still works and is still the accessible path. This is
    // the same action reached the way the pack invites: press it and keep
    // pressing. Ported from the Slabz rip gesture -- grace, then a charging
    // ring, then a burst -- with the drag guard this stage needs because our
    // pack spins under the same finger.
    const [holding, setHolding] = useState(false)
    /** Shown after a press too short to have started the hold. The gesture is
     *  not discoverable on its own -- Slabz answers a tap with "hold to rip",
     *  and without it a player taps the pack, nothing happens, and they
     *  conclude it is not the control. */
    const [hint, setHint] = useState(false)
    const [progress, setProgress] = useState(0)
    const [burst, setBurst] = useState(false)
    const graceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const doneRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const rafRef = useRef(0)
    const originRef = useRef<{ x: number; y: number } | null>(null)

    const pressedAtRef = useRef(0)

    const stopHold = useCallback(() => {
        sound.stop('hold')
        // Released before the charge even began: they tapped. Say what the
        // control wants instead of doing nothing.
        if (pressedAtRef.current && Date.now() - pressedAtRef.current < HOLD_GRACE_MS) {
            setHint(true)
            setTimeout(() => setHint(false), 1600)
        }
        pressedAtRef.current = 0
        if (graceRef.current) clearTimeout(graceRef.current)
        if (doneRef.current) clearTimeout(doneRef.current)
        if (rafRef.current) cancelAnimationFrame(rafRef.current)
        graceRef.current = null
        doneRef.current = null
        rafRef.current = 0
        originRef.current = null
        setHolding(false)
        setProgress(0)
    }, [])

    // Every timer and frame is torn down on unmount. A pack switched away from
    // mid-hold would otherwise fire `onOpen` for a pack nobody is looking at,
    // which on this page costs money.
    useEffect(() => stopHold, [stopHold])

    const startHold = (e: React.PointerEvent) => {
        if (!available || busy || launching) return
        // Stops the browser turning the press into a text selection or a
        // long-press callout mid-gesture, which is what `WebkitTouchCallout`
        // and `select-none` guard against on the Slabz stage.
        e.preventDefault()
        setHint(false)
        originRef.current = { x: e.clientX, y: e.clientY }
        pressedAtRef.current = Date.now()
        graceRef.current = setTimeout(() => {
            setHolding(true)
            // Starts with the charge, not with the press: the grace window
            // exists precisely so a flick to spin makes no sound at all.
            sound.play('hold', 0.5)
            const started = Date.now()
            const tick = () => {
                const p = Math.min((Date.now() - started) / HOLD_MS, 1)
                setProgress(p)
                if (p < 1) rafRef.current = requestAnimationFrame(tick)
            }
            rafRef.current = requestAnimationFrame(tick)

            doneRef.current = setTimeout(() => {
                stopHold()
                setBurst(true)
                // The burst plays before the reveal takes the stage, so the
                // gesture visibly completes rather than the screen simply
                // changing under the finger.
                sound.play('hit', 0.6)
                setTimeout(() => {
                    setBurst(false)
                    onOpen()
                }, 350)
            }, HOLD_MS)
        }, HOLD_GRACE_MS)
    }

    const maybeCancelOnMove = (e: React.PointerEvent) => {
        const from = originRef.current
        if (!from) return
        if (Math.hypot(e.clientX - from.x, e.clientY - from.y) > DRAG_CANCEL_PX) stopHold()
    }

    const RING = 2 * Math.PI * 34

    return (
        <div className='flex h-full min-h-0 w-full flex-col items-center gap-4'>
            {/* The pack, as a real 3D model. It idles with a slow turn and plays
                its baked turntable flourish the moment a spin launches (driven by
                `launching`), just before the reveal reels take the stage.

                `flex-1` over a fixed height: this is the one part of the column
                that can give, so it absorbs whatever the viewport leaves after
                the name and the action block have taken theirs. The width cap
                keeps the frame portrait — the canvas holds a fixed vertical FOV,
                so letting it go wide and short would crop the pack. */}
            <div className='relative min-h-[180px] w-full max-w-[380px] flex-1 drop-shadow-[0_40px_110px_rgba(3,206,255,0.4)]'>
                {/* The gesture layer sits AROUND the canvas, not on it: Pack3D
                    owns its own pointer handlers for the spin, and putting these
                    on the same element would make every drag a hold attempt. */}
                <div
                    className={twMerge(
                        'h-full w-full select-none transition-transform duration-300 ease-out',
                        // Slabz lifts and grows the pack while it is held, and
                        // wobbles it while it opens. Without the first one the
                        // gesture gives nothing back for two seconds and reads
                        // as broken; without the second the open is a cut.
                        holding && 'scale-[1.04] -translate-y-1',
                        launching && 'animate-[packRip_2s_ease-in-out_infinite]',
                        available && !busy && !launching && 'cursor-pointer',
                    )}
                    style={{ touchAction: 'none', WebkitTouchCallout: 'none' } as any}
                    onPointerDown={startHold}
                    onPointerMove={maybeCancelOnMove}
                    onPointerUp={stopHold}
                    onPointerLeave={stopHold}
                    onPointerCancel={stopHold}
                >
                    {/* The art the pack was given in team tools, not the one
                        baked into the component. `artFor` falls back rather
                        than rendering an empty stage for a pack written before
                        the field existed. */}
                    <Pack3D
                        frontArt={artFor(pack.art).front}
                        backArt={artFor(pack.art).back}
                        spinToken={launching ? 1 : 0}
                        className='h-full w-full'
                    />
                </div>

                {/* What a tap gets. Slabz shows this in the middle of the pack
                    after a short press, and it is the only thing that teaches
                    the gesture. */}
                {hint && !holding && !launching && (
                    <div className='pointer-events-none absolute left-1/2 top-1/2 z-20 -translate-x-1/2 -translate-y-1/2'>
                        <span
                            className='whitespace-nowrap rounded-lg border px-4 py-2 text-xs font-black uppercase tracking-widest backdrop-blur-sm'
                            style={{
                                borderColor: 'rgba(3,206,255,0.35)',
                                color: '#03CEFF',
                                background: 'rgba(0,0,0,0.8)',
                            }}
                        >
                            Hold to open
                        </span>
                    </div>
                )}

                {/* The charging ring. Plain SVG and a dash offset rather than an
                    animation library -- this repo has no framer-motion and one
                    stroke does not justify adding it. */}
                {holding && (
                    <div className='pointer-events-none absolute left-1/2 top-1/2 z-20 -translate-x-1/2 -translate-y-1/2'>
                        {/* The arc painted NOTHING, which is why the ring never
                            filled: `ACCENT` is a CSS gradient string
                            ('linear-gradient(...)'), and SVG `stroke` does not
                            accept one -- it takes a paint value, so the whole
                            declaration was dropped. The gradient is declared
                            properly here as an SVG <linearGradient> and
                            referenced by id, which is both the fix and the way
                            to keep the brand ramp rather than falling back to a
                            flat colour. */}
                        <svg width='80' height='80' viewBox='0 0 80 80'>
                            <defs>
                                <linearGradient id='packHoldRing' x1='0' y1='0' x2='1' y2='1'>
                                    <stop offset='0%' stopColor='#03CEFF' />
                                    <stop offset='100%' stopColor='#C91FFF' />
                                </linearGradient>
                            </defs>
                            <circle
                                cx='40'
                                cy='40'
                                r='34'
                                fill='none'
                                stroke='rgba(255,255,255,0.12)'
                                strokeWidth='5'
                            />
                            <circle
                                cx='40'
                                cy='40'
                                r='34'
                                fill='none'
                                stroke='url(#packHoldRing)'
                                strokeWidth='5'
                                strokeLinecap='round'
                                strokeDasharray={RING}
                                strokeDashoffset={RING * (1 - progress)}
                                transform='rotate(-90 40 40)'
                            />
                        </svg>
                    </div>
                )}

                {/* The completion burst. One expanding ring, so the gesture ends
                    with something rather than the stage simply cutting away. */}
                {burst && (
                    <div
                        className='pointer-events-none absolute left-1/2 top-1/2 z-20 -translate-x-1/2 -translate-y-1/2 animate-[packBurst_350ms_ease-out_forwards] rounded-full border-2'
                        // Same trap as the ring above: a gradient is not a
                        // border colour. One end of the brand ramp, which is a
                        // real colour.
                        style={{ borderColor: '#03CEFF', width: 60, height: 60 }}
                    />
                )}

                {/* The label alone, over the pack rather than over a curtain.
                    The full-bleed black wash hid the thing it was labelling --
                    the art is what somebody is looking at, and "sold out" is a
                    fact ABOUT it, not a reason to stop showing it. The pack can
                    still be spun underneath. */}
                {!bare && (soldOut || unreleased) && (
                    <div className='pointer-events-none absolute inset-x-0 top-4 grid place-items-center'>
                        <span
                            className={twMerge(
                                'border px-4 py-1.5 text-xs font-black uppercase tracking-[0.25em] backdrop-blur-sm',
                                soldOut
                                    ? 'border-red-500/60 bg-red-950/40 text-red-400'
                                    : 'border-amber-400/60 bg-amber-950/40 text-amber-400',
                            )}
                        >
                            {soldOut ? 'Sold out' : 'Unavailable'}
                        </span>
                    </div>
                )}
            </div>

            {/* Name + the open action. Fades out on spin, in step with the left
                rail, leaving the pack alone on the stage. */}
            <div
                className={twMerge(
                    'flex w-full max-w-sm shrink-0 animate-rise-in flex-col items-center gap-2 transition-all duration-500',
                    launching && 'pointer-events-none translate-y-3 opacity-0',
                )}
            >
                {!bare && (
                    <div className='text-center text-lg font-black uppercase tracking-[0.12em]'>
                        {pack.name}
                    </div>
                )}
                <button
                    onClick={onOpen}
                    disabled={!available || busy}
                    style={available ? { background: ACCENT } : undefined}
                    className={twMerge(
                        'inline-flex w-full items-center justify-center gap-3 px-8',
                        ACTION_ROW_H,
                        'text-sm font-black uppercase tracking-[0.2em] transition-[filter]',
                        available
                            ? 'hover:brightness-110 disabled:opacity-40'
                            : 'cursor-not-allowed border border-white/15 text-white/40',
                    )}
                >
                    <img
                        src={skull}
                        alt=''
                        aria-hidden='true'
                        className='h-5 w-5 object-contain'
                        style={{ filter: 'brightness(0) invert(1)' }}
                    />
                    {busy
                        ? 'Spinning…'
                        : bare
                          ? 'Spin'
                          : available
                            ? `Spin · $${pack.price}`
                            : soldOut
                              ? 'Sold out'
                              : 'Unavailable'}
                </button>
                {/* Second row of the action block, matching the rail's second
                    info button. It carries the stock line, which used to sit
                    above the button and pushed it down out of alignment. */}
                <div
                    className={twMerge(
                        'flex w-full items-center justify-center text-center text-2xs font-bold uppercase tracking-[0.2em] text-white/40',
                        ACTION_ROW_H,
                    )}
                >
                    {blockedReason ? (
                        <span className='text-amber-400/80 normal-case tracking-normal'>{blockedReason}</span>
                    ) : (
                        !bare &&
                        available &&
                        /* A skin pack with no supply set never runs out, and its
                           `stock` is a sentinel rather than a count. */
                        (pack.unlimited ? 'Unlimited' : `${pack.stock} weapons left`)
                    )}
                </div>
            </div>
        </div>
    )
}
